import {Badge, Button, PopoverClose} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {RosterPopoverContent, RosterPopoverDivider, RosterPopoverRow} from '@/components';

import {memberDisplayName} from '../utils';
import {MemberAvatar} from './MemberAvatar';

import type {TeamPerson} from '../types';
import type {TrustAccountMemberRole} from '@sollapay/enums';
import type {FC} from 'react';

export interface TeamRosterPopoverContentProps {
  trustee: TeamPerson | null;
  teammates: (TeamPerson & {role: TrustAccountMemberRole})[];
  /** Runs after the popover closes — takes the reader to the account's team. */
  onManage: () => void;
}

/** The account's people in a dropdown — the trustee first, then the team with their roles, and a link to manage them. */
export const TeamRosterPopoverContent: FC<TeamRosterPopoverContentProps> = ({
  trustee,
  teammates,
  onManage
}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <RosterPopoverContent
      title={t('view.header.team.dropdownTitle')}
      count={teammates.length + (trustee ? 1 : 0)}
      footer={
        <PopoverClose
          render={
            <Button variant="linkColor" className="w-full justify-start" onClick={onManage} />
          }
        >
          {t('view.header.team.manage')}
        </PopoverClose>
      }
    >
      {trustee && (
        <>
          <RosterPopoverRow
            avatar={<MemberAvatar user={trustee} size="default" />}
            name={memberDisplayName(trustee)}
            caption={trustee.email ?? t('members.trusteeBadge')}
            tag={
              <Badge variant="brand-subtle" size="sm" className="shrink-0">
                {t('view.header.team.trusteeLabel')}
              </Badge>
            }
          />
          <RosterPopoverDivider />
        </>
      )}
      {teammates.map(member => (
        <RosterPopoverRow
          key={member.userId}
          avatar={<MemberAvatar user={member} size="default" />}
          name={memberDisplayName(member)}
          caption={t(`members.roles.${member.role}.one`)}
        />
      ))}
    </RosterPopoverContent>
  );
};
